import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/auth.js";
import Button from "@/components/ui/Button.jsx";
import Icon from "@/components/ui/Icon.jsx";

/*
 * Route guard.
 *
 *   <Route element={<RequireAuth />}>                     signed in
 *   <Route element={<RequireAuth role="superAdmin" />}>   …and this role
 *   <Route element={<RequireAuth role={ORG_ROLES} />}>    …or any of these
 *
 * The Next.js app only hid the Super Admin nav item; typing /super-admin still
 * rendered the page and let the backend reject each request one at a time. This
 * closes that.
 *
 * `?next=` sends you back where you were after signing in, which the current app
 * does not do — landing on the dashboard after being bounced from a deep link is
 * a small, constant annoyance.
 */
export default function RequireAuth({ role }) {
    const { isAuthenticated, verifying, endedReason, hasRole } = useAuth();
    const location = useLocation();

    // A stored token that has not been checked yet. Rendering the page would fire
    // requests that may be about to 401; bouncing to /login would sign out a
    // perfectly good session on a slow network. So: wait.
    if (verifying) return <AuthPending />;

    if (!isAuthenticated) return <Navigate to={loginTarget(endedReason, location)} replace />;

    // One role or a list — the organization routes admit three.
    if (role && !hasRole(...[].concat(role))) return <Forbidden />;

    return <Outlet />;
}

/*
 * The only place that decides where an unauthenticated visitor goes.
 *
 *   session expired   → ?error=session-expired, so /login can explain itself
 *   signed out        → bare /login; sending them back to where they just left
 *                       would fight an intentional sign-out
 *   never signed in   → ?next=…, so a deep link survives the detour
 */
function loginTarget(endedReason, location) {
    if (endedReason === "session-expired") return "/login?error=session-expired";
    if (endedReason === "signed-out") return "/login";

    const next = location.pathname + location.search;
    if (!next || next === "/" || next === "/dashboard") return "/login";
    return `/login?next=${encodeURIComponent(next)}`;
}

function AuthPending() {
    return (
        <div className="flex min-h-screen items-center justify-center">
            <div className="flex items-center gap-3 text-[13px] text-slate-400 dark:text-slate-500">
                <svg
                    className="size-5 animate-spin"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    aria-hidden="true"
                >
                    <path d="M21 12a9 9 0 1 1-6.22-8.56" />
                </svg>
                Checking your session…
            </div>
        </div>
    );
}

function Forbidden() {
    const { role } = useAuth();

    return (
        <main className="mx-auto flex w-full max-w-lg flex-col items-center gap-4 p-6 py-24 text-center">
            <span className="grid size-12 place-items-center rounded-full bg-rose-50 text-rose-600 dark:bg-rose-950/60 dark:text-rose-400">
                <Icon name="shield" className="size-6" />
            </span>

            <h1 className="text-[20px] font-semibold tracking-tight">Not available to your role</h1>
            <p className="text-[13.5px] leading-relaxed text-slate-500 dark:text-slate-400">
                This page needs the <strong className="font-semibold">super admin</strong> role. You are
                signed in as <strong className="font-semibold">{role}</strong>.
            </p>

            <Button to="/dashboard" variant="primary" className="mt-2">
                Go to dashboard
            </Button>
        </main>
    );
}
